import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useSurahList } from '@/hooks/useSurah';
import { revelationLabel } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';
import { IconSearch } from '@/components/home/icons';
import { SurahIndexSeo } from '@/components/seo/SurahIndexSeo';

type Filter = 'all' | 'Meccan' | 'Medinan';
const FILTERS: Filter[] = ['all', 'Meccan', 'Medinan'];

/**
 * Surah index — the full list of all 114 surahs, in the dark "night/beam"
 * theme adapted from the Stitch design. Live search + revelation filter.
 */
export function SurahIndex() {
  const { data, isLoading, isError, refetch } = useSurahList();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const surahs = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return surahs.filter((s) => {
      if (filter !== 'all' && revelationLabel(s.revelation_type) !== filter) {
        return false;
      }
      if (!q) return true;
      return (
        s.name_en.toLowerCase().includes(q) ||
        (s.name_translation ?? '').toLowerCase().includes(q) ||
        s.name_ar.includes(query.trim()) ||
        String(s.id) === q
      );
    });
  }, [surahs, query, filter]);

  return (
    <section className="container-wide pb-20 pt-12 sm:pt-16">
      {/* Per-route SEO meta — only renders once the surah list loads
          (we need the names + numbers to build the ItemList JSON-LD). */}
      {data && data.length > 0 && <SurahIndexSeo surahs={data} />}

      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
          The Quran
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-night-50 sm:text-4xl">
          All 114 Surahs of the Quran
          {/* SEO subtitle inside the same H1 so the target keyword sits
              inside the page's primary heading without breaking visual
              hierarchy. */}
          <span className="mt-3 block text-base font-medium tracking-normal text-night-300 sm:text-lg">
            Read the Quran online with English &amp; Urdu translation
          </span>
        </h1>
        <p className="mt-4 max-w-2xl text-night-200">
          Browse the full list of Quran chapters in order — Arabic name, English
          meaning, Meccan or Medinan revelation, and verse count. Tap any surah
          to read it with side-by-side English (Sahih International) and Urdu
          (Jalandhari) translation, plus audio recitation by Mishary Alafasy.
        </p>
      </header>

      {/* Controls */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative flex w-full items-center sm:max-w-xs">
          <IconSearch className="pointer-events-none absolute left-4 h-4 w-4 text-night-300" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or number…"
            aria-label="Search surahs"
            className="h-11 w-full rounded-full border border-night-500/50 bg-night-800/70 pl-11 pr-4 text-sm text-night-50 placeholder:text-night-300 transition-colors focus:border-beam focus:outline-none"
          />
        </label>

        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-all',
                filter === f
                  ? 'bg-beam text-beam-ink'
                  : 'border border-night-500/40 bg-night-800 text-night-200 hover:border-beam/50 hover:text-beam-pale',
              )}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <SurahGridSkeleton />
      ) : isError ? (
        <ErrorPanel onRetry={() => refetch()} />
      ) : surahs.length === 0 ? (
        <Notice>
          No surahs were returned. The <code>quran_core.surahs</code> table
          looks empty — the canonical count is 114.
        </Notice>
      ) : filtered.length === 0 ? (
        <Notice>
          No surah matches{' '}
          <span className="text-night-50">“{query}”</span>. Try another name or
          number.
        </Notice>
      ) : (
        <>
          <p className="mb-5 text-sm text-night-300">
            {filtered.length} {filtered.length === 1 ? 'surah' : 'surahs'}
          </p>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/surah/${s.id}`}
                  className="group flex h-full flex-col rounded-2xl border border-night-500/40 bg-night-700/60 p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-beam/60 hover:bg-night-700"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display text-sm font-bold text-beam">
                      {String(s.id).padStart(3, '0')}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-night-300">
                      {revelationLabel(s.revelation_type)}
                    </span>
                  </div>
                  <p
                    dir="rtl"
                    className="mt-4 font-arabic text-3xl leading-tight text-night-50"
                  >
                    {s.name_ar}
                  </p>
                  <h2 className="mt-3 font-display text-lg font-bold text-night-50">
                    {s.name_en}
                  </h2>
                  <p className="mt-auto pt-2 text-xs text-night-300">
                    {s.name_translation ? (
                      <span className="italic">{s.name_translation} · </span>
                    ) : null}
                    {s.ayah_count} ayahs
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function SurahGridSkeleton() {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 12 }).map((_, i) => (
        <li
          key={i}
          className="h-44 animate-pulse rounded-2xl border border-night-500/30 bg-night-700/40"
        />
      ))}
    </ul>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-night-500/40 bg-night-800/60 p-10 text-center text-night-200">
      {children}
    </div>
  );
}

function ErrorPanel({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-night-500/40 bg-night-800/60 p-10 text-center">
      <p className="text-night-100">We couldn&apos;t load the surah list.</p>
      <p className="mt-1 text-sm text-night-300">
        Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 inline-flex h-10 items-center justify-center rounded-xl bg-beam px-6 text-sm font-semibold text-beam-ink transition-transform hover:scale-105 active:scale-95"
      >
        Try again
      </button>
    </div>
  );
}
